import { useEffect } from "react";
import { useSelector } from "react-redux";
import { App as CapacitorApp } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";

import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline, StyledEngineProvider } from "@mui/material";

// routing
import Routes from "routes";

// defaultTheme
import themes from "themes";

// project imports
import NavigationScroll from "features/layout/NavigationScroll";
import Logger from "utils/logger";
import NotificationHandler from "components/NotificationHandler";
import MobileToastPositioner from "components/MobileToastPositioner";
import admobService from "native/admob";
import purchaseService from "features/membership/purchases";
import AdBanner from "components/AdBanner";
import remoteConfigService from "data/firestore/remoteConfig";
import SupabaseDocument from "data/supabase/document";
import FirestoreDocument from "data/firestore/document";
import { disableNetwork, enableNetwork } from "firebase/firestore";
import { db } from "config/firebase";
import {
  isVersionOutdated,
  getVersionUpdateMessage,
  getCurrentVersion,
} from "native/version";
import useCustomSnackbar from "hooks/customSnackbar";
import AnalyticsService from "data/firestore/analytics";
import { initializeAppCheckForPlatform } from "config/appCheck";

// ==============================|| APP ||============================== //

const App = () => {
  const customization = useSelector((state) => state.customization);
  const { enqueueSnackbar } = useCustomSnackbar();

  useEffect(() => {
    // Check if app version is outdated and show warning if needed
    const checkVersion = () => {
      try {
        const currentVersion = getCurrentVersion();
        const latestVersion = remoteConfigService.latestVersion;
        Logger.debug(
          "App: Version check - Current:",
          currentVersion,
          "Latest from Firebase:",
          latestVersion
        );
        Logger.debug(
          "App: Remote Config cache status:",
          remoteConfigService.cacheStatus
        );

        if (isVersionOutdated(currentVersion, latestVersion)) {
          const message = getVersionUpdateMessage(
            currentVersion,
            latestVersion
          );
          Logger.warn("App: Version outdated, showing warning:", message);
          enqueueSnackbar(message, {
            variant: "warning",
            persist: true,
            autoHideDuration: 10000, // Show for 10 seconds
          });
        } else {
          Logger.debug("App: Version is up to date");
        }
        AnalyticsService.event("app_loaded", {
          version: currentVersion,
          mobile: Capacitor.isNativePlatform(),
        });
      } catch (error) {
        Logger.error("App: Error checking version:", error);
        AnalyticsService.event("app_loaded", {
          version: "UNKNOWN",
          mobile: Capacitor.isNativePlatform(),
        });
      }
    };

    // Wait for remote config to be ready
    const waitForConfig = async () => {
      let attempts = 0;
      const maxAttempts = 20; // Wait up to 10 seconds (20 * 500ms)

      while (!remoteConfigService.isInitialized && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        attempts++;
      }

      if (remoteConfigService.isInitialized) {
        // Force refresh remote config to get latest values
        Logger.debug("App: Forcing remote config refresh...");
        await remoteConfigService.refresh();

        // Debug remote config values
        Logger.debug("App: Debugging remote config...");
        const debugInfo = remoteConfigService.debugRemoteConfig();
        Logger.debug("App: Remote config debug info:", debugInfo);

        checkVersion(); // Check version after config is ready
      } else {
        Logger.warn("App: Remote Config failed to initialize within timeout");
      }
    };

    // Initialize Admob service on mobile only
    const initializeAdmob = async () => {
      try {
        if (Capacitor.isNativePlatform()) {
          Logger.debug("🔥 APP: Initializing Admob service...");
          await admobService.initialize();
          Logger.debug("🔥 APP: Admob service initialized successfully");
        } else {
          Logger.debug(
            "🔥 APP: Skipping Admob initialization - not mobile platform"
          );
        }
      } catch (error) {
        Logger.error("🔥 APP: Error initializing Admob service:", error);
        // Don't let Admob errors crash the app
      }
    };

    // Initialize Remote Config service
    const initializeRemoteConfig = async () => {
      try {
        Logger.debug("🔥 APP: Initializing Remote Config service...");
        await remoteConfigService.initialize();
        Logger.debug("🔥 APP: Remote Config initialized successfully");
        Logger.debug(
          "🔥 APP: Gatekeepers enabled:",
          remoteConfigService.shouldEnforceGatekeepers
        );
      } catch (error) {
        Logger.error(
          "🔥 APP: Error initializing Remote Config service:",
          error
        );
        // Don't let Remote Config errors crash the app
      }
    };

    // App Check must initialize before other services make Firestore requests
    initializeAppCheckForPlatform().then(() => {
      initializeAdmob();
      initializeRemoteConfig();
      purchaseService.initialize();

      // Wait for remote config to be ready
      waitForConfig();
    });

    if (!Capacitor.isNativePlatform()) {
      return;
    }

    // Register Android back button listener (following the guide's approach)
    const registerAndroidBackButton = () => {
      if (Capacitor.getPlatform() === "android") {
        const backButtonListener = CapacitorApp.addListener(
          "backButton",
          (data) => {
            if (data.canGoBack) {
              window.history.back();
            } else {
              CapacitorApp.exitApp();
            }
          }
        );
        return backButtonListener;
      }
      return null;
    };

    // Register Android back button listener
    const backButtonListener = registerAndroidBackButton();

    // On resume from background, drop cached read promises so pages refetch
    // fresh instead of re-awaiting anything left mid-flight when we were frozen.
    const resumeListener = CapacitorApp.addListener(
      "appStateChange",
      (state) => {
        if (state.isActive) {
          SupabaseDocument.resetCache();
          FirestoreDocument.resetCache();
          // Firestore's realtime socket can go half-open while we were frozen:
          // onSnapshot listeners (chat) then never redeliver and only an app
          // restart recovers. Bounce the network to drop the stale socket and
          // force a reconnect so live listeners resume on their own.
          disableNetwork(db)
            .then(() => enableNetwork(db))
            .catch(() => {});
        }
      }
    );

    // Clean up the listeners when the component is unmounted
    // This is a best practice to avoid memory leaks
    return () => {
      if (backButtonListener) {
        backButtonListener.remove();
      }
      resumeListener.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes(customization)}>
        <CssBaseline />
        <NavigationScroll>
          <Routes />
        </NavigationScroll>
        <NotificationHandler />
        <MobileToastPositioner />
        <AdBanner />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
